import { useEffect, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch } from "react-redux";

import { PayloadAction } from "@reduxjs/toolkit";

import { CatchmentSettingsFormValues } from "@/components/spatialAnalysis/catchmentSettings";
import { debounce } from "@/utils/debounce";

type CatchmentArrayField = "radiusData" | "distances" | "durations";

type CatchmentSyncPayload = Partial<Record<CatchmentArrayField, string[]>>;

const DEFAULT_DEBOUNCE_MS = 400;

/** Debounce only the Redux write that drives the map preview; the input stays
 * instant (react-hook-form owns the value). useMemo, not useCallback: it must
 * keep the same debounced instance (its timer) across per-keystroke re-renders.
 *
 * @param syncAction Pass a slice action creator, not an inline arrow: a new
 * identity per render re-creates the debounce and cancels its pending timer.
 */
export const useDebouncedCatchmentSync = (
  syncAction: (
    payload: CatchmentSyncPayload | null
  ) => PayloadAction<CatchmentSyncPayload | null>,
  delay: number = DEFAULT_DEBOUNCE_MS
) => {
  const { getValues } = useFormContext<CatchmentSettingsFormValues>();
  const dispatch = useDispatch();

  const debouncedSync = useMemo(
    () =>
      debounce((field: CatchmentArrayField) => {
        const values = getValues(field)?.filter(
          (item) => item && item !== " " && item !== "0"
        );
        const payload: CatchmentSyncPayload = { [field]: values };
        dispatch(syncAction(payload));
      }, delay),
    [dispatch, getValues, syncAction, delay]
  );

  useEffect(() => () => debouncedSync.cancel(), [debouncedSync]);

  return debouncedSync;
};
